import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, LogOut, Save, RefreshCw, Upload, Trash2, Plus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";
const inputCls = "w-full bg-[#1E2334] border border-ice/20 focus:border-brand focus:ring-2 focus:ring-brand/40 rounded-lg px-4 py-3 text-white placeholder-white/30 outline-none transition-[border-color,box-shadow] duration-300 font-light text-sm";

const fmtErr = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e.msg).join(" ") : "Something went wrong");

function PageEditor({ token }) {
  const [pages, setPages] = useState([]);
  const [sel, setSel] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/pages`).then((r) => setPages(r.data)).catch(() => {});
  }, []);

  const load = async (slug) => {
    setSel(slug);
    setDraft(null);
    try {
      const r = await axios.get(`${API}/pages/${slug}`);
      setDraft(r.data);
    } catch {
      toast.error("Could not load page");
    }
  };

  const upload = async (file, idx) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/upload`, fd, auth);
      const sections = [...draft.sections];
      sections[idx] = { ...sections[idx], image: r.data.storage_path };
      setDraft({ ...draft, sections });
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/pages/${sel}`, { ...draft, bullets: draft.bullets.filter((b) => b.trim()) }, auth);
      toast.success("Page published — live on the site now.");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setSection = (idx, patch) => {
    const sections = [...draft.sections];
    sections[idx] = { ...sections[idx], ...patch };
    setDraft({ ...draft, sections });
  };

  return (
    <div data-testid="cms-panel" className="mt-8 rounded-xl border border-ice/15 bg-white/[0.03] p-8">
      <h2 className="font-display font-black uppercase tracking-tight text-xl">Pages & Terms (CMS)</h2>
      <p className="mt-2 text-white/50 font-light text-xs">Edit titles, texts and photos for each product page and the Terms & Conditions. Changes go live instantly.</p>
      <div className="mt-6 flex flex-wrap gap-2">
        {pages.map((p) => (
          <button
            key={p.slug}
            data-testid={`cms-page-${p.slug}`}
            onClick={() => load(p.slug)}
            className={`font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-2 rounded-full transition-colors duration-300 ${sel === p.slug ? "bg-brand text-white" : "border border-white/15 text-white/60 hover:text-white"}`}
          >
            {p.title || p.slug}
          </button>
        ))}
      </div>

      {sel && !draft && <Loader2 className="w-5 h-5 animate-spin text-brand mt-8" />}

      {draft && (
        <div className="mt-8 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2">Page title</span>
              <input data-testid="cms-title-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className={inputCls} />
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2">Tagline</span>
              <input data-testid="cms-tagline-input" value={draft.tagline} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })} className={inputCls} />
            </label>
          </div>
          <label className="block">
            <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2">Bullet points (one per line)</span>
            <textarea
              data-testid="cms-bullets-input"
              rows={5}
              value={draft.bullets.join("\n")}
              onChange={(e) => setDraft({ ...draft, bullets: e.target.value.split("\n") })}
              className={inputCls}
            />
          </label>

          <div className="space-y-4">
            {draft.sections.map((s, i) => (
              <div key={i} data-testid={`cms-section-${i}`} className="rounded-lg border border-white/10 bg-[#1E2334] p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">Section {i + 1}</span>
                  <button
                    data-testid={`cms-section-delete-${i}`}
                    onClick={() => setDraft({ ...draft, sections: draft.sections.filter((_, j) => j !== i) })}
                    className="text-white/40 hover:text-brand transition-colors duration-200"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <input data-testid={`cms-section-heading-${i}`} value={s.heading} onChange={(e) => setSection(i, { heading: e.target.value })} placeholder="Section heading" className={inputCls} />
                <textarea data-testid={`cms-section-text-${i}`} rows={4} value={s.text} onChange={(e) => setSection(i, { text: e.target.value })} placeholder="Section text" className={inputCls} />
                <div className="flex items-center gap-4 flex-wrap">
                  {s.image && <img src={`${API}/media/${s.image}`} alt="" className="h-16 w-24 object-cover rounded-lg border border-white/10" />}
                  <label className="flex items-center gap-2 border border-dashed border-white/20 hover:border-brand rounded-lg px-4 py-3 cursor-pointer transition-colors duration-300">
                    <Upload className="w-4 h-4 text-brand" />
                    <span className="text-xs text-white/60">{s.image ? "Replace photo" : "Add photo"}</span>
                    <input data-testid={`cms-section-image-${i}`} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0], i)} />
                  </label>
                  {s.image && (
                    <button data-testid={`cms-section-image-remove-${i}`} onClick={() => setSection(i, { image: "" })} className="text-xs text-white/40 hover:text-brand transition-colors duration-200">
                      Remove photo
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3 flex-wrap">
            <button
              data-testid="cms-add-section"
              onClick={() => setDraft({ ...draft, sections: [...draft.sections, { heading: "", text: "", image: "" }] })}
              className="flex items-center gap-2 border border-white/15 text-white/60 hover:text-white font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 rounded-full transition-colors duration-300"
            >
              <Plus className="w-3.5 h-3.5" /> Add section
            </button>
            <button
              data-testid="cms-save-button"
              disabled={busy}
              onClick={save}
              className="flex items-center gap-2 bg-brand hover:bg-ice hover:text-ink text-white font-display font-bold uppercase tracking-wide text-sm px-7 py-2.5 rounded-full transition-colors duration-300 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Publish page
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("so_admin_token") || "");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [leads, setLeads] = useState([]);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async (t = token) => {
    if (!t) return;
    try {
      const [p, l] = await Promise.all([
        axios.get(`${API}/pricing`),
        axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${t}` } }),
      ]);
      setPricing(p.data);
      setLeads(l.data);
    } catch {
      localStorage.removeItem("so_admin_token");
      setToken("");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/login`, login);
      localStorage.setItem("so_admin_token", res.data.token);
      setToken(res.data.token);
      load(res.data.token);
      toast.success("Welcome back, owner.");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const savePricing = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.put(`${API}/pricing`, { ...pricing, shop_price: +pricing.shop_price, apps_price: +pricing.apps_price, annual_price: +pricing.annual_price, discount_percent: +pricing.discount_percent }, auth);
      toast.success("Pricing updated — live on the site now.");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!token || !pricing) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-ink text-white flex items-center justify-center px-6">
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          onSubmit={doLogin}
          className="w-full max-w-md rounded-xl border border-white/15 bg-white/[0.05] p-10 card-glow"
        >
          <div className="font-display font-black text-2xl tracking-tighter uppercase">simply<span className="text-brand">Owner</span></div>
          <p className="mt-2 font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">Owner access only</p>
          <div className="mt-8 space-y-4">
            <input data-testid="admin-email-input" type="email" required placeholder="Email" value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} className={inputCls} />
            <input data-testid="admin-password-input" type="password" required placeholder="Password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} className={inputCls} />
          </div>
          <button data-testid="admin-login-button" disabled={busy} className="mt-6 w-full flex items-center justify-center gap-2 bg-brand hover:bg-ice text-white hover:text-ink font-display font-bold uppercase tracking-wide px-8 py-3.5 rounded-full transition-colors duration-300 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Sign in
          </button>
          <a href="/" data-testid="admin-back-link" className="block mt-6 text-center font-mono text-[10px] tracking-[0.2em] uppercase text-white/30 hover:text-ice transition-colors duration-300">Back to site</a>
        </motion.form>
      </div>
    );
  }

  const NumField = ({ label, k, step = "1" }) => (
    <label className="block">
      <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2">{label}</span>
      <input data-testid={`pricing-${k}-input`} type="number" min="0" step={step} required value={pricing[k]} onChange={(e) => setPricing({ ...pricing, [k]: e.target.value })} className={inputCls} />
    </label>
  );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink text-white px-6 lg:px-12 py-16 [&>*]:max-w-6xl [&>*]:mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="font-display font-black text-3xl tracking-tighter uppercase">Owner <span className="text-brand">Panel</span></div>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mt-2">Pricing control · Leads · Demo bookings</p>
        </div>
        <div className="flex gap-3">
          <button data-testid="admin-refresh-button" onClick={() => load()} className="flex items-center gap-2 border border-ice/30 text-ice hover:bg-ice/10 font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 rounded-full transition-colors duration-300">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={() => { localStorage.removeItem("so_admin_token"); setToken(""); setPricing(null); }}
            className="flex items-center gap-2 border border-white/15 text-white/60 hover:text-white font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <form onSubmit={savePricing} data-testid="pricing-editor" className="lg:col-span-2 rounded-xl border border-brand/40 bg-white/[0.03] p-8 card-glow h-fit">
          <h2 className="font-display font-black uppercase tracking-tight text-xl">Pricing & Discounts</h2>
          <p className="mt-2 text-white/50 font-light text-xs">Changes go live on the landing page instantly.</p>
          <div className="mt-6 space-y-4">
            <NumField label="Shop system price (CHF, one-time)" k="shop_price" />
            <NumField label="Shop + apps price (CHF, one-time)" k="apps_price" />
            <NumField label="Annual costs (CHF / year)" k="annual_price" />
            <NumField label="Discount (%)" k="discount_percent" step="0.5" />
            <label className="block">
              <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2">Discount label</span>
              <input data-testid="pricing-label-input" type="text" value={pricing.discount_label} onChange={(e) => setPricing({ ...pricing, discount_label: e.target.value })} placeholder="Summer launch offer" className={inputCls} />
            </label>
            <label className="flex items-center gap-3 cursor-pointer pt-1">
              <input data-testid="pricing-discount-toggle" type="checkbox" checked={pricing.discount_active} onChange={(e) => setPricing({ ...pricing, discount_active: e.target.checked })} className="w-4 h-4 accent-[#D10056]" />
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/60">Discount active on site</span>
            </label>
          </div>
          <button data-testid="pricing-save-button" disabled={busy} className="mt-6 w-full flex items-center justify-center gap-2 bg-brand hover:bg-ice text-white hover:text-ink font-display font-bold uppercase tracking-wide px-8 py-3.5 rounded-full transition-colors duration-300 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save & publish
          </button>
        </form>

        <div data-testid="leads-panel" className="lg:col-span-3 rounded-xl border border-ice/15 bg-white/[0.03] p-8">
          <h2 className="font-display font-black uppercase tracking-tight text-xl">Leads & Demo Bookings</h2>
          <p className="mt-2 text-white/50 font-light text-xs" data-testid="leads-count">{leads.length} request{leads.length === 1 ? "" : "s"} total</p>
          <div className="mt-6 space-y-3 max-h-[560px] overflow-y-auto pr-2">
            {leads.length === 0 && <p className="text-white/40 font-light text-sm">No requests yet. Share your page.</p>}
            {leads.map((l, i) => (
              <div key={i} data-testid={`lead-item-${i}`} className="rounded-lg border border-white/10 bg-[#1E2334] px-5 py-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="font-display font-bold text-sm">{l.name} — <span className="text-ice">{l.restaurant}</span></span>
                  <span className={`font-mono text-[9px] tracking-[0.2em] uppercase px-3 py-1 rounded-full ${l.type === "demo" ? "bg-brand/20 text-ice" : l.type === "signup" ? "bg-brand text-white" : "bg-white/10 text-white/60"}`}>
                    {l.type === "demo" ? `Demo · ${l.date} ${l.time}` : l.type === "signup" ? "Signup · 50% committed" : "Lead"}
                  </span>
                </div>
                <div className="mt-1.5 text-xs text-white/50 font-light flex flex-wrap items-center gap-x-2">
                  <span>{l.email}{l.phone ? ` — ${l.phone}` : ""}{l.message ? ` — ${l.message}` : ""}</span>
                  {l.files && Object.values(l.files).map((f) => (
                    <a key={f.storage_path} data-testid={`lead-file-${f.original_filename}`} href={`${API}/files/${f.storage_path}?auth=${token}`} target="_blank" rel="noreferrer" className="text-brand underline underline-offset-2">
                      {f.original_filename}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <PageEditor token={token} />
    </div>
  );
}
